import { useEffect, useRef, useState } from "react";
import { Send, Sparkles, Check } from "lucide-react";

const LOGO =
    "https://customer-assets.emergentagent.com/job_ff479c40-d417-47dd-a43d-49b94fe41eee/artifacts/y09pkqnz_07_TopParent_Logomark_Icon.png";

const DEFAULT_FEEDBACK = ["\u{1F629}", "\u{1F615}", "\u{1F610}", "\u{1F60A}", "\u{1F60D}"];

/* Sequence durations (ms). Total loop ~18s. */
const T = {
    typeStart: 600,
    typeStep: 28,
    botTyping: 5400,
    intro: 6400,
    matchPanel: 7800,
    card: 9400,
    chips: 12400,
    feedback: 13800,
    restart: 18000,
};

const TypingDots = () => (
    <div className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl bg-white shadow-soft">
        <span className="block h-1.5 w-1.5 rounded-full bg-[#0D1B2A]/40 animate-bounce [animation-delay:-0.2s]" />
        <span className="block h-1.5 w-1.5 rounded-full bg-[#0D1B2A]/40 animate-bounce [animation-delay:-0.1s]" />
        <span className="block h-1.5 w-1.5 rounded-full bg-[#0D1B2A]/40 animate-bounce" />
    </div>
);

const FadeIn = ({ show, children, className = "" }) =>
    show ? <div className={`animate-fade-up ${className}`}>{children}</div> : null;

/**
 * AnimatedChatDemo
 *
 * Re-usable animated chat demo. Pass a `script` describing the parent
 * message, friendly intro reply, matching panel rows, activity card,
 * and (optional) action chips / feedback emoji row.
 *
 * Fixed-size phone screen with internal scroll so the latest message
 * stays visible, exactly like a real WhatsApp chat.
 *
 * script shape:
 *   {
 *     parentMessage: string,
 *     introReply: string,
 *     matchingRows: [{ label, value }],
 *     activity: { title, summary, whyFits, steps: string[] },
 *     actions: string[],            // optional
 *     feedback: string[],           // optional
 *     caption: string,              // optional, shown under the phone
 *     headerSubtitle: string,       // optional, default "Activity planner for parents"
 *   }
 */
const AnimatedChatDemo = ({
    script,
    testId = "animated-chat-demo",
    onPhaseChange,
    compact = false,
    glow = true,
}) => {
    const {
        parentMessage,
        introReply,
        matchingRows = [],
        activity,
        actions,
        feedback = DEFAULT_FEEDBACK,
        caption = "Example flow. The activity flow is being built now.",
        headerSubtitle = "Activity planner for parents",
    } = script;

    const [tick, setTick] = useState(0);
    const [typed, setTyped] = useState("");
    const [parentDone, setParentDone] = useState(false);
    const [showThinking, setShowThinking] = useState(false);
    const [showIntro, setShowIntro] = useState(false);
    const [showMatch, setShowMatch] = useState(false);
    const [showCard, setShowCard] = useState(false);
    const [showChips, setShowChips] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);

    const scrollerRef = useRef(null);
    const onPhaseChangeRef = useRef(onPhaseChange);
    useEffect(() => {
        onPhaseChangeRef.current = onPhaseChange;
    }, [onPhaseChange]);
    const emitPhase = (phase) => {
        if (typeof onPhaseChangeRef.current === "function") {
            onPhaseChangeRef.current(phase);
        }
    };

    useEffect(() => {
        const timers = [];

        setTyped("");
        setParentDone(false);
        setShowThinking(false);
        setShowIntro(false);
        setShowMatch(false);
        setShowCard(false);
        setShowChips(false);
        setShowFeedback(false);
        emitPhase(0);

        timers.push(
            setTimeout(() => {
                let i = 0;
                const iv = setInterval(() => {
                    i += 1;
                    if (i > parentMessage.length) {
                        clearInterval(iv);
                        setParentDone(true);
                        return;
                    }
                    setTyped(parentMessage.slice(0, i));
                }, T.typeStep);
                timers.push({ clear: () => clearInterval(iv) });
            }, T.typeStart),
        );

        timers.push(setTimeout(() => setShowThinking(true), T.botTyping));
        timers.push(
            setTimeout(() => {
                setShowThinking(false);
                setShowIntro(true);
                emitPhase(1);
            }, T.intro),
        );
        timers.push(setTimeout(() => setShowMatch(true), T.matchPanel));
        timers.push(
            setTimeout(() => {
                setShowCard(true);
                emitPhase(2);
            }, T.card),
        );
        if (actions && actions.length > 0) {
            timers.push(setTimeout(() => setShowChips(true), T.chips));
        }
        timers.push(
            setTimeout(() => {
                setShowFeedback(true);
                emitPhase(3);
            }, T.feedback),
        );
        timers.push(setTimeout(() => setTick((n) => n + 1), T.restart));

        return () => {
            timers.forEach((t) => {
                if (typeof t === "number") clearTimeout(t);
                else if (t && typeof t.clear === "function") t.clear();
            });
        };
    }, [tick, parentMessage, actions]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }, [typed, showThinking, showIntro, showMatch, showCard, showChips, showFeedback]);

    return (
        <div className="relative max-w-[400px] mx-auto" data-testid={testId}>
            {glow && (
                <div className="absolute -inset-6 bg-gradient-to-tr from-brand-skyBlue/30 via-transparent to-brand-softOrange/25 rounded-[2.2rem] blur-2xl opacity-70 pointer-events-none" />
            )}

            <div className="relative rounded-[2rem] bg-[#0D1B2A] p-3 shadow-hover ring-1 ring-brand-navy/[0.08]">
                <div
                    className={`rounded-[1.5rem] bg-[#ECE5DD] overflow-hidden flex flex-col ${
                        compact ? "h-[540px] sm:h-[580px]" : "h-[600px] sm:h-[640px]"
                    }`}
                >
                    <div className="flex items-center gap-3 bg-[#075E54] text-white px-4 py-3 shrink-0">
                        <img
                            src={LOGO}
                            alt=""
                            className="h-9 w-9 rounded-full bg-white/95 p-0.5 ring-1 ring-white/30 object-contain"
                        />
                        <div className="min-w-0">
                            <p className="text-[13.5px] font-semibold leading-tight">
                                TopParent
                            </p>
                            <p className="text-[11.5px] text-white/75 leading-tight">
                                {headerSubtitle}
                            </p>
                        </div>
                        <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.14em] font-bold text-white/80 bg-white/10 rounded-full px-2 py-0.5">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                            Demo
                        </span>
                    </div>

                    <div
                        ref={scrollerRef}
                        className="flex-1 overflow-y-auto px-3.5 py-4 space-y-2.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                    >
                        {/* Parent message */}
                        <div className="flex justify-end">
                            <div
                                className={`max-w-[90%] rounded-2xl rounded-tr-md bg-[#DCF8C6] px-3.5 py-2.5 text-[13px] text-[#0D1B2A] leading-snug shadow-soft transition-opacity ${
                                    typed.length > 0 || parentDone ? "opacity-100" : "opacity-0"
                                }`}
                            >
                                <p className="leading-[1.5]">
                                    {parentDone ? parentMessage : typed}
                                    {!parentDone && (
                                        <span className="inline-block w-[6px] h-[14px] align-middle bg-[#0D1B2A]/50 ml-0.5 animate-blink" />
                                    )}
                                </p>
                                <span className="block text-right text-[10.5px] mt-1 text-[#0D1B2A]/55">
                                    just now
                                </span>
                            </div>
                        </div>

                        {showThinking && (
                            <div className="flex justify-start animate-fade-up">
                                <TypingDots />
                            </div>
                        )}

                        <FadeIn show={showIntro} className="flex justify-start">
                            <div className="max-w-[92%] rounded-2xl rounded-tl-md bg-white px-3.5 py-2.5 text-[13px] text-[#0D1B2A] leading-[1.55] shadow-soft border border-[#0D1B2A]/[0.06]">
                                {introReply}
                                <span className="block text-right text-[10.5px] mt-1 text-[#0D1B2A]/45">
                                    just now
                                </span>
                            </div>
                        </FadeIn>

                        <FadeIn show={showMatch} className="flex justify-start">
                            <div className="max-w-[92%] rounded-2xl rounded-tl-md bg-white px-3.5 py-3 text-[12.5px] text-[#0D1B2A] shadow-soft border border-[#0D1B2A]/[0.06]">
                                <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] font-bold text-brand-coral mb-2">
                                    <Sparkles size={11} strokeWidth={2.25} />
                                    Matching this activity to
                                </div>
                                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px] leading-[1.4]">
                                    {matchingRows.map((r) => (
                                        <div key={r.label} className="contents">
                                            <span className="text-[#475569]">{r.label}</span>
                                            <span className="font-semibold text-[#0D1B2A]">
                                                {r.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </FadeIn>

                        <FadeIn show={showCard} className="flex justify-start">
                            <div className="max-w-[94%] rounded-2xl rounded-tl-md bg-white px-4 py-3.5 text-[12.5px] text-[#0D1B2A] shadow-soft border border-[#0D1B2A]/[0.06]">
                                <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-brand-coral mb-1.5">
                                    Today&rsquo;s activity
                                </p>
                                <p className="font-heading text-[16px] font-bold text-[#0D1B2A] leading-tight mb-1">
                                    {activity.title}
                                </p>
                                <p className="text-[11.5px] text-[#475569] mb-3">
                                    {activity.summary}
                                </p>
                                <p className="text-[12px] text-[#334155] leading-[1.5] mb-2">
                                    <span className="font-semibold text-[#0D1B2A]">Why this fits.</span>{" "}
                                    {activity.whyFits}
                                </p>
                                <p className="text-[11.5px] font-semibold text-[#0D1B2A] mt-2.5 mb-1">
                                    Play steps
                                </p>
                                <ol className="space-y-1 text-[11.5px] text-[#334155] leading-[1.5]">
                                    {activity.steps.map((s, i) => (
                                        <li key={s} className="flex gap-2">
                                            <span className="text-brand-coral font-bold tabular-nums w-3 shrink-0">
                                                {i + 1}.
                                            </span>
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </FadeIn>

                        {actions && actions.length > 0 && (
                            <FadeIn show={showChips}>
                                <div className="flex flex-wrap gap-1.5 pl-1 pt-0.5">
                                    {actions.map((a, i) => (
                                        <span
                                            key={a}
                                            className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11.5px] font-semibold ${
                                                i === 0
                                                    ? "bg-brand-coral text-white"
                                                    : "bg-white text-brand-navy border border-[#0D1B2A]/10"
                                            }`}
                                        >
                                            {i === 0 && <Check size={11} strokeWidth={2.5} />}
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            </FadeIn>
                        )}

                        <FadeIn show={showFeedback}>
                            <div className="rounded-2xl bg-white border border-[#0D1B2A]/[0.06] shadow-soft px-3.5 py-2.5 inline-flex items-center gap-2">
                                <span className="text-[11.5px] text-[#475569] mr-1">
                                    How did it go?
                                </span>
                                {feedback.map((e) => (
                                    <span
                                        key={e}
                                        className="text-[17px] leading-none transition-transform hover:scale-125 cursor-default select-none"
                                    >
                                        {e}
                                    </span>
                                ))}
                            </div>
                        </FadeIn>
                    </div>

                    <div className="px-3 pb-3 shrink-0">
                        <div className="bg-white rounded-full flex items-center gap-2 px-4 py-2 shadow-soft">
                            <span className="text-[12px] text-[#0D1B2A]/45 flex-1 truncate">
                                Tell TopParent about today&hellip;
                            </span>
                            <Send size={14} strokeWidth={2} className="text-brand-coral" />
                        </div>
                    </div>
                </div>
            </div>

            {!compact && (
                <p className="mt-4 text-center text-[12.5px] text-[#475569]">{caption}</p>
            )}
        </div>
    );
};

export default AnimatedChatDemo;
